import { Flex, IconListFilled } from '@gmg/gmg-react-components';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchBox from 'src/components/shared/SearchBox';
import ActivityTable from './ActivityTable';
import { ActivityListViewModel, ActivityViewModel } from './activityViewModel';
import BreadCrumbItem from '../shared/BreadCrumbItem';

type ActivityListContainerProps = {
    data?: ActivityListViewModel;
    isFetching: boolean;
    requestPage: (cursor: string | null) => void;
    isGmGAdminView?: boolean;
    // Only used when isGmGAdminView is false: the wrapping OrganizationContainer owns the header/breadcrumb/search box.
    filterValue?: string;
};

const mergeUniqueById = (previous: ActivityViewModel[], incoming: ActivityViewModel[]) => {
    const merged = new Map<string, ActivityViewModel>();

    previous.forEach((item) => merged.set(item.id, item));
    incoming.forEach((item) => merged.set(item.id, item));

    return Array.from(merged.values());
};

const ActivityListContainer: FC<ActivityListContainerProps> = ({
    data,
    isFetching,
    requestPage,
    isGmGAdminView = false,
    filterValue: filterValueProp,
}) => {
    const { t } = useTranslation();
    const [internalFilterValue, setInternalFilterValue] = useState<string>('');
    const filterValue = isGmGAdminView ? internalFilterValue : (filterValueProp ?? '');
    const [activities, setActivities] = useState<ActivityViewModel[]>([]);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const requestedCursorRef = useRef<string | null>(null);
    const isInitialLoading = isFetching && activities.length === 0;
    const isLoadingMore = isFetching && activities.length > 0;

    const requestNextPage = useCallback((cursor: string | null) => {
        requestPage(cursor);
    }, [requestPage]);

    useEffect(() => {
        requestNextPage(null);
    }, [requestNextPage]);

    useEffect(() => {
        if (!data) return;
        const incomingActivities = data.activities || [];

        setActivities((prev) => mergeUniqueById(prev, incomingActivities));
        setNextCursor(data.nextCursor);
    }, [data]);

    useEffect(() => {
        if (!isFetching) {
            // Clear the locally blocked cursor once the request cycle finishes so the next page can be requested.
            requestedCursorRef.current = null;
        }
    }, [isFetching]);

    const handleLoadMore = () => {
        if (
            isFetching
            || !nextCursor
            || requestedCursorRef.current === nextCursor
        ) return;

        requestedCursorRef.current = nextCursor;
        requestNextPage(nextCursor);
    };

    return (
        <Flex flexDirection="column" gap="2px" bg="background" width="100%">
            {/* For the org-scoped view, the wrapping OrganizationContainer already renders the breadcrumb/title/search box. */}
            {isGmGAdminView && (
                <Flex width="100%" height="64px" gap="2px" bg="background">
                    <Flex width="100%" height="100%" justifyContent="space-between" alignItems="center" bg="backgroundPaper" pr="24px" pl="24px" data-testid="activity-title-bar">
                        <BreadCrumbItem
                            label={t('HeaderPageTitles.all_activity_title', 'Activity')}
                            icon={<IconListFilled width={16} height={16}/>}
                            isHighlighted
                        />
                        <SearchBox
                            onChange={(e) => setInternalFilterValue(e.target.value)}
                            value={internalFilterValue}
                        />
                    </Flex>
                </Flex>
            )}

            <ActivityTable
                rows={activities}
                isInitialLoading={isInitialLoading}
                isLoadingMore={isLoadingMore}
                hasNextPage={!!nextCursor}
                onLoadMore={handleLoadMore}
                filterValue={filterValue}
                showTargetOrganizationColumn={isGmGAdminView}
            />
        </Flex>
    );
};

export default ActivityListContainer;
